import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Button, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useIsMobile } from "../../../hooks/useMobile";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { INK, NEUTRAL, STATUS, SURFACE } from "../../../theme/tokens";
import { AgentActionType, AgentActionTypeDisplay, AgentStaffActionStatus, CallStatus, type FlatNurseAction } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { useRunNurseActionMutation } from "../hooks/useAgentHistory";

interface ActionsAcrodionsProps {
	actions: FlatNurseAction[];
	requestId: string | null | undefined;
	displayFullDate?: boolean;
}

interface GroupedAction {
	actionType: AgentActionType;
	hour: number;
	actions: FlatNurseAction[];
}

export const ActionsAcrodions: FC<ActionsAcrodionsProps> = ({ actions, requestId, displayFullDate }) => {
	const theme = useTheme();
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const isMobile = useIsMobile();
	const runNurseActionMutation = useRunNurseActionMutation(requestId);
	const now = DateTime.now();

	// Group actions by action type and hour
	const groupedActions = useMemo(() => {
		const groups = new Map<string, GroupedAction>();

		actions.forEach((action) => {
			if (!action.action_time) return;

			const actionTime = DateTime.fromISO(action.action_time);
			const hour = actionTime.hour;
			const actionType = action.type as AgentActionType;

			const key = `${actionType}-${hour}`;
			if (!groups.has(key)) {
				groups.set(key, {
					actionType,
					hour,
					actions: [],
				});
			}

			groups.get(key)?.actions.push(action);
		});

		groups.forEach((group) => {
			group.actions.sort((a, b) => {
				if (!a.action_time || !b.action_time) return 0;
				return DateTime.fromISO(a.action_time).valueOf() - DateTime.fromISO(b.action_time).valueOf();
			});
		});

		return Array.from(groups.values()).sort((a, b) => {
			const aFirstTime = a.actions[0]?.action_time;
			const bFirstTime = b.actions[0]?.action_time;

			if (!aFirstTime && !bFirstTime) return 0;
			if (!aFirstTime) return 1;
			if (!bFirstTime) return -1;

			const aTime = DateTime.fromISO(aFirstTime).valueOf();
			const bTime = DateTime.fromISO(bFirstTime).valueOf();

			if (aTime !== bTime) {
				return aTime - bTime;
			}

			return a.actionType.localeCompare(b.actionType);
		});
	}, [actions]);

	const getHoursFromNow = (actionTime: string): string => {
		const time = DateTime.fromISO(actionTime);
		const diff = time.diff(now, ["days", "hours", "minutes"]);
		const days = Math.round(diff.days);
		const hours = Math.round(diff.hours);
		const minutes = Math.round(diff.minutes);

		if (days === 0 && hours === 0) {
			if (minutes === 0) {
				return "now";
			}
			if (minutes > 0) {
				return `in ${minutes} minute${minutes !== 1 ? "s" : ""}`;
			}
			return `${Math.abs(minutes)} minute${Math.abs(minutes) !== 1 ? "s" : ""} ago`;
		}

		if (days === 0) {
			if (hours > 0) {
				return `in ${hours} hour${hours !== 1 ? "s" : ""}`;
			}
			return `${Math.abs(hours)} hour${Math.abs(hours) !== 1 ? "s" : ""} ago`;
		}

		if (days > 0) {
			return `in ${days} day${days !== 1 ? "s" : ""}`;
		}

		return `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`;
	};

	const getActionIcon = (action: AgentActionType): React.ElementType => {
		return AgentActionTypeDisplay[action]?.icon ?? AgentActionTypeDisplay[AgentActionType.Regular].icon;
	};

	const getTitleText = (staffCount: number, actionType: AgentActionType, actions: FlatNurseAction[]) => {
		const staffText = staffCount === 1 ? "staff member" : "staff members";

		const now = DateTime.now();
		const futureCount = actions.filter((action) => {
			if (!action.action_time) return false;
			return DateTime.fromISO(action.action_time) > now;
		}).length;

		const pastCount = actions.length - futureCount;

		let statusText = "";
		if (futureCount === 0) {
			statusText = actionType === AgentActionType.Message ? "sent" : "made";
		} else if (pastCount === 0) {
			statusText = actionType === AgentActionType.Message ? "will send" : "will make";
		} else {
			statusText = "ongoing";
		}

		if (actionType === AgentActionType.Message) {
			return `Messages ${statusText} (${staffCount} ${staffText})`;
		}
		if (actionType === AgentActionType.Call) {
			return `Calls ${statusText} (${staffCount} ${staffText})`;
		}
		const actionTypeLabel = AgentActionTypeDisplay[actionType]?.label || actionType;
		return `${actionTypeLabel} ${statusText} (${staffCount} ${staffText})`;
	};

	const getCallStatusIcon = (action: FlatNurseAction) => {
		if (action.type !== AgentActionType.Call) return <DoneAllIcon sx={{ fontSize: "14px", color: INK.SECONDARY }} />;
		switch (action.response_status) {
			case CallStatus.Completed:
				return <PhoneEnabledIcon sx={{ fontSize: "14px", color: INK.SECONDARY }} />;
			case CallStatus.NoAnswer:
			case CallStatus.Busy:
				return <PhoneDisabledIcon sx={{ fontSize: "14px", color: STATUS.ERROR.main }} />;
			case CallStatus.Failed:
			case CallStatus.Canceled:
				return <CancelIcon sx={{ fontSize: "14px", color: STATUS.ERROR.main }} />;
			case CallStatus.Queued:
			case CallStatus.Ringing:
			case CallStatus.InProgress:
				return <AccessTimeIcon sx={{ fontSize: "14px", color: NEUTRAL[500] }} />;
			default:
				return null;
		}
	};

	return (
		<>
			{groupedActions.length === 0 ? (
				<Box sx={{ py: 4, textAlign: "center" }}>
					<Typography variant="body2" sx={{ color: NEUTRAL[500] }}>
						No actions found
					</Typography>
				</Box>
			) : (
				groupedActions.map((group, index) => {
					const firstActionTime = group.actions[0]?.action_time;
					const hourLabel =
						displayFullDate && firstActionTime
							? DateTime.fromISO(firstActionTime).toFormat("EEE, MMM d, h:mm a")
							: firstActionTime
								? getHoursFromNow(firstActionTime)
								: "";

					const IconComponent = getActionIcon(group.actionType);

					return (
						<Accordion
							key={`${group.actionType}-${group.hour}-${index}`}
							sx={{ border: `1px solid ${NEUTRAL[150]}`, borderRadius: "4px", mb: 1, "&:before": { display: "none" } }}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: isMobile ? "center" : "start",
										flexDirection: isMobile ? "row" : "column",
										justifyContent: "space-between",
										width: "100%",
										gap: 1,
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
										{IconComponent && (
											<Avatar
												sx={{
													backgroundColor: theme.palette.primary.main,
													width: 24,
													height: 24,

													"& .MuiSvgIcon-root": {
														fontSize: "14px",
														color: SURFACE,
													},
												}}
											>
												<IconComponent />
											</Avatar>
										)}
										<Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: isMobile ? "12px" : "14px" }}>
											{getTitleText(group.actions.length, group.actionType, group.actions)}
										</Typography>
									</Box>
									<Typography variant="body2" sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "12px", fontWeight: 400 }}>
										{hourLabel}
									</Typography>
								</Box>
							</AccordionSummary>
							<AccordionDetails
								sx={{
									px: 2,
									py: 1,
									pt: 0,
									maxHeight: "300px",
									overflowY: "auto",
								}}
							>
								{group.actions.map((action) => {
									const nurseName = getNurseFormattedName(action.nurse_id);
									const hoursFromNow = action.action_time ? getHoursFromNow(action.action_time) : "N/A";
									const timeDisplay = displayFullDate && action.action_time ? DateTime.fromISO(action.action_time).toFormat("h:mm a") : hoursFromNow;
									const isFuture = action.action_time ? DateTime.fromISO(action.action_time) > now : false;

									return (
										<Box
											key={`${action.user_id}-${action.action_time}`}
											sx={{
												padding: "8px 16px",
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												backgroundColor: alpha(NEUTRAL[150], 0.2),
												py: 1,
												borderBottom: `1px solid ${NEUTRAL[150]}`,
												"&:hover": {
													backgroundColor: NEUTRAL[150],
													"& .action-reminder-btn": {
														opacity: 1,
													},
												},
												"&:last-child": { borderBottom: "none" },
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												{isFuture && <AccessTimeIcon sx={{ fontSize: "16px", color: NEUTRAL[500] }} />}
												{action.is_approved && (
													<InHouseTooltip title="Accepted">
														<CheckCircleOutlineIcon sx={{ fontSize: "16px", color: STATUS.SUCCESS.main }} />
													</InHouseTooltip>
												)}
												<Typography variant="body2" sx={{ color: theme.palette.primary.main, fontSize: "12px", fontWeight: 600 }}>
													{nurseName || `Nurse ${action.nurse_id}`}
												</Typography>
											</Box>
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
												{action.status === AgentStaffActionStatus.Loading ? (
													<CircularProgress size={12} sx={{ color: theme.palette.primary.main }} />
												) : (
													isFuture && (
														<Button
															size="small"
															variant="text"
															className="action-reminder-btn"
															onClick={(event) => {
																event.stopPropagation();
																runNurseActionMutation.mutate({ nurseActionId: action.id, isFuture });
															}}
															disabled={runNurseActionMutation.isPending}
															sx={{
																fontSize: "11px",
																textTransform: "none",
																padding: 0,
																minWidth: "auto",
																opacity: 0,
																transition: "opacity 0.2s",
																color: theme.palette.primary.main,
															}}
														>
															Act now
														</Button>
													)
												)}
												<Typography variant="caption" sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "12px", fontWeight: 400 }}>
													{timeDisplay}
												</Typography>
												{!isFuture && <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>{getCallStatusIcon(action)}</Box>}
											</Box>
										</Box>
									);
								})}
							</AccordionDetails>
						</Accordion>
					);
				})
			)}
		</>
	);
};
